/**
 * References:
 *      aria-autocomplete
 *      http://msdn.microsoft.com/en-us/library/windows/apps/hh968002.aspx
 *
 *      WAI-ARIA support for AutoComplete widget
 *      http://www.javacodegeeks.com/2014/10/wai-aria-support-for-autocomplete-widget.html
 *
 *      activedescendant
 *      http://test.cita.illinois.edu/aria/combobox/combobox3.php
 *
 * Template file in: sass\components\_form-selector.scss
 */

import util from './util';

const _cache = {}; // we don't really need to use the cache as the ajax() method has its own cache.
const SEARCH_MIN_DEFAULT = 1;
let _ignoreNextFocusEvent;
let _ignoreNextBlurEvent;

let _input; // input field to enhance
let _btn; // The fields search/add button
let _shadow; // code field
let _list; // container element referenced by <input aria-owns> to put the search results into
let _options; // search results as an array of listitem elements
let _timer; // There's some funkyness happening when the user clicks on a record. The blur event fires, but we don't want to remove the click event handler until the click has been handled.  A click could be on a list item.  If it isn't we use the timeout to hide the listbox, else we hide the list box if a list item was selected.
let _searchmin; // min number of text user needs to enter before we fire off a search

const breakpoints = {
    large: /* only screen and */ '(min-width: 768px)',
    medium: /* only screen and */ '(min-width: 481px)',
    small: /* only screen and */ '(max-width: 480px)',
};

function nullifyStateVariables() {
    _input = null;
    _btn = null;
    _shadow = null;
    _list = null;
    _options = null;
    _searchmin = null;
}

// Other important variables
// input.lastSelectedDescription - Used to determine if the user has entered text ... if the user has not entered text, then we search for blank, not the defaulted value.
// input.oldval                  - When the user cancels (presses the escape key) we need to restore the previous state

const KEYS = {
    up: 38,
    down: 40,
    escape: 27,
    enter: 13,
};
const _modifiers = [16, 17, 18, 91, 93, 224]; // shift, control, alt(Option), windows, context, meta(Command)
const ARIA_SELECTED = 'aria-selected';
const ARIA_EXPANDED = 'aria-expanded';

function init(addedNodes) {
    addedNodes.forEach((addedNode) => {
        const autos = addedNode.querySelectorAll('input[aria-autocomplete][role=combobox]');

        Array.from(autos).forEach((node) => {
            if (node._autocomplete) return;

            try {
                initialiseAutocompleteField(node);
            } catch (e) {
                console.log(e);
            }
        });
    });
}

function initialiseAutocompleteField(node) {
    node._autocomplete = true;

    if (node.hasAttribute('data-display') || node.getAttribute('data-descfield')) {
        initShadowField(node);
    }

    node.lastSelectedDescription = node.value; // used when the user clicks search button, search for default list, not on defaulted text

    // autocomplete filters have a delete button
    const removeBtn = node.parentElement.querySelector('button.fa-xmark');
    if (removeBtn) {
        if (node.value) removeBtn.classList.remove('d-none');
        removeBtn.addEventListener('click', () => clearAutocomplete(node));
    }

    const btnSearch = getButton(node, 'button.fa-magnifying-glass');

    if (btnSearch) {
        btnSearch.setAttribute('data-btn-for', node.id); // we need to re-apply state variables for the matching auto-select field when the button is clicked as last field focused might not be the correct field
        syncSearchButton(node); // disable button if user shouldn't be able to search yet
        btnSearch.addEventListener('click', handleButtonClick);
    }

    // add users to Optional Recipients list
    const btnAdd = getButton(node, 'button.fa-plus');

    if (btnAdd) {
        btnAdd.setAttribute('data-btn-for', node.id);
        btnAdd.addEventListener('click', handleAddButtonClick);
    }

    const btnAddRecipient = document.getElementById('btnAddRecipient');
    const btnRemoveRecipient = document.getElementById('btnRemoveRecipient');

    if (btnAddRecipient) {
        btnAddRecipient.addEventListener('click', (e) => {
            checkIfForgotAddRecipient(btnAdd, node, e);
        });
    }

    if (btnRemoveRecipient) {
        btnRemoveRecipient.addEventListener('click', (e) => {
            checkIfForgotAddRecipient(null, node, e);
        });
    }

    // restore default placeholder for filtered fields
    const placeholder = node.getAttribute('data-placeholder');
    if (placeholder) node.placeholder = placeholder;

    initParams(node);
    const params = node.getAttribute('data-propagate');

    if (params) {
        try {
            node['data-propagate'] = JSON.parse(params);
        } catch (ex) {
            console.warn('error parsing attribute data-propagate for field %s', node.id);
        }
    }

    const list = getListElement(node);
    list.setAttribute('aria-expanded', false);
    list.addEventListener('click', handleListOptionClick); // when the user selects an item in the dropdown list - update the code/description
    getListWrap(node).parentElement.addEventListener('click', handleListWrapClick); // when the user clicks outside of the list - close the list (mobile mode only)

    // listen for "load more" button clicks
    util.each(getListWrap(node).querySelectorAll('[data-ac-loadmore]'), (b) => {
        b.addEventListener('click', handleLoadMoreClicked);
    });

    node.addEventListener('focus', handleInputFocus);
    node.addEventListener('blur', handleInputBlur); // this could be done each time the field is "in focus" but we would have to manage unlistening

    _cache[node.id] = {};
    dispatch(node, 'autocompleteinitialised', {
        input: node,
        shadow: getShadowField(node),
    });
    nullifyStateVariables();
}

function checkIfForgotAddRecipient(btnAdd, node, e) {
    e.preventDefault();

    if (btnAdd) {
        btnAdd.click();
    } else {
        clearAutocomplete(node);
    }

    node.classList.remove('form-control-danger');
    util.showElement(document.getElementById('extraNotificationMsg'), false);
}

// ---------------------------------------------------------------------------------------------
//
//   F O C U S  /  B L U R   E V E N T   H A N D L E R S
//
// ---------------------------------------------------------------------------------------------

/**
 * Set the internal vars and eventListeners when the input[role=autocomplete] receives focus
 *
 * Called when field clicked or tabbed into.
 *
 * @param  {event|HTMLInputElement} e [description]
 * @return {[type]}   [description]
 */
function handleInputFocus(e) {
    if (_ignoreNextFocusEvent) {
        _ignoreNextFocusEvent = null;
        return;
    }

    const input = e.tagName ? e : e.target;

    if (input === _input /* && isBreakpointSmall() */) {
        // if the focused field is already being managed
        // nothing to do
    } else {
        if (_input) unwatch(true); // force cleanup in case a previously selected autocomplete field had focus

        _input = input;
        _input.fixupPending = true; // let the page's _submit() method know this field may have a pending blur event to execute

        if (_input.value === _input.lastSelectedDescription) {
            // if the user hasn't changed the value, then save the cancel=>restore data
            const shadow = getShadowField(_input);
            input.oldval = shadow ? { input: input.value, shadow: shadow.value } : input.value;
        }

        input.addEventListener('keyup', handleKeyupEvent); // prevent enter key from submitting form
        input.addEventListener('keydown', handleKeydownEvent); // handle up/down/enter/escape key presses
    }

    if (!isBreakpointSmall() && !input.readOnly) {
        // autoshow the list in desktop/tablet mode only
        syncSearchList(); // auto-search on empty text will only happen if searchmin == 0
    }
}

/**
 * Remove eventListeners when the input[role=autocomplete] loses focus
 *
 * @param  {[type]} e [description]
 * @return {[type]}   [description]
 */
function handleInputBlur(e) {
    const node = e.target;

    if (_ignoreNextBlurEvent) {
        _ignoreNextBlurEvent = false;
        _input.focus();
        return;
    }

    // if an autocomplete field is cleared ensure that the shadow field is also cleared
    if (!node.value) clearAutocomplete(node);

    node.removeEventListener('keyup', handleKeyupEvent);
    node.removeEventListener('keydown', handleKeydownEvent);
    unwatch();
}

/**
 * The search button was clicked ... execute the search then show the results
 *
 * @param  {[type]} e [description]
 * @return {[type]}   [description]
 */
function handleButtonClick(e) {
    const btn = e.target;
    const input = document.getElementById(btn.getAttribute('data-btn-for'));

    if (!input) return console.warn('Could not find target input text field for clicked button');

    if (input.disabled) return;

    handleInputFocus(input); // pretend the user focused the input text field
    // setFocused();
    syncSearchList(); // execute the search
    if (!isBreakpointSmall()) input.focus(); // focus the input field
}

function addRecipientDivToList(isN900, fullUserName, userId, input) {
    const notificationListField = isN900
        ? 'N900F040_NOTIFICATION_LIST'
        : 'N902F045_NOTIFICATION_LIST';
    const inputId = `${notificationListField}_${userId}`;
    const querySelector = isN900
        ? `#${notificationListField} .checkbox-wrap`
        : `#${notificationListField}-ct .checkbox-wrap`;

    const div = document.createElement('div');
    div.className = 'form-check';
    div.innerHTML = `<input type="checkbox" class="form-check-input" id=${inputId} name=${notificationListField} value=${userId} checked="checked">
         <label class="form-check-label" for=${inputId}>${fullUserName}</label>`;
    document.querySelector(querySelector).appendChild(div);
    clearAutocomplete(input);
}

/**
 * The Add button was clicked... add new optional recipients from input of ADD MORE REIPENTS
 *
 * @param  {[type]} e [description]
 * @return {[type]}   [description]
 */
function handleAddButtonClick(e) {
    const btn = e.target;
    const input = document.getElementById(btn.getAttribute('data-btn-for'));

    if (!input) return console.warn('Could not find target input text field for clicked button');
    if (input.disabled || !input.value) return;

    const isN900 = btn.getAttribute('data-btn-for').startsWith('N900');
    const userName = input.value.split('(')[0].trim();
    const userId = getShadowField(input).value;

    // all recipients include mandatory and optional
    const allRecipientsList = document.querySelectorAll('.form-check-input');
    const ifNoChangeInInput = input.lastSelectedDescription === input.value;
    const duplicatedRecipient = Array.from(allRecipientsList).some(
        (node) => node.value === (ifNoChangeInInput ? userId : input.value),
    );

    if (duplicatedRecipient) {
        notification.notify(
            'Warning',
            `Recipient (${userName}) already exists, please select again.`,
            1,
        );
        clearAutocomplete(input);
    } else if (ifNoChangeInInput) {
        // 1. No change in Input after select an user
        addRecipientDivToList(isN900, input.value, userId, input);
    } else {
        // 2. Change input value after selecting an user
        const url = `${input.getAttribute('data-action').replace('srch', 'rslv')}/${input.value}`;

        ajax({
            url,
            documentType: 'json',
            callback(response) {
                if (!response || !response.T803F005_USER) {
                    notification.notify('Warning', 'Invalid user name', 1);
                    clearAutocomplete(input);
                } else {
                    addRecipientDivToList(
                        isN900,
                        response.T803F020_NAME,
                        response.T803F005_USER,
                        input,
                    );
                }
            },
        });
    }
}

function handleMouseDown(e) {
    if (_input) {
        // a code picker is currently being managed
        let node = e.target;
        const wrap = getListWrap(_input);
        let clickedOnLi = false;

        while (node) {
            if (node.nodeName.toLowerCase() === 'li') clickedOnLi = true;

            if (node === wrap) {
                if (!clickedOnLi) {
                    // if the user clicked in the dropdown list, but not a list item
                    _ignoreNextBlurEvent = true;
                }

                return; // else the user clicked in a list item so don't ignore blur event
            }

            node = node.parentNode;
        }
    }
}

/* prevent body scrolling when the user is trying to scroll the list */
function handleMouseWheel(e) {
    if (!isDropdownOpen()) return;

    const ct = $(e.target).parents('.listbox-container')[0]; // the scroll container

    if (!ct) return;

    const isAtEndOfList =
        e.deltaY < 0 ? ct.scrollTop === 0 : ct.scrollTop === ct.scrollHeight - ct.clientHeight;

    if (isAtEndOfList) {
        e.stopPropagation();
    }
}

/**
 * This method should only be callable in mobile (small) view mode
 *
 * @return {[type]}   [description]
 */
function handleListWrapClick() {
    closeDropdown(); // hide the wrap
    _input.select(); // and re-focus the input field
}

/**
 * Set the selected option by clickEvent
 */
function handleListOptionClick(e) {
    let node = e.target;

    while (node && 'li' !== node.nodeName.toLowerCase()) node = node.parentNode;

    if (node) {
        e.preventDefault(); // don't bubble

        // used by modal.js when checking if modal contains clicked element
        window.autocomplete.selectedListOption = node;

        if (node.hasAttribute('data-ignore')) return;

        selectListOption(node); // update the values in description and shadow fields
        const shadow = getShadowField(_input);
        (shadow || _input).dispatchEvent(
            new CustomEvent('autocompleteitemselected', {
                detail: node,
            }),
        ); // inform watchers the field has changed

        closeDropdown(); // hide the wrap
        _ignoreNextFocusEvent = true;
        _input.select(); // and re-focus the input field
    }
}

/**
 * [unwatch description]
 * @param  {[type]} immediate [description]
 * @return {[type]}           [description]
 *
 *
 * Final step of the onBlur handler
 */
function unwatch(immediate) {
    immediate =
        0 < arguments.length
            ? !!immediate // typecast to boolean
            : false; // else set to default

    if (immediate) {
        // the user has tabbed from one autocomplete field to another
        unwatchForReal();
        _timer = clearTimeout(_timer);
    } else if (
        isBreakpointSmall() &&
        getListWrap(_input).parentElement.classList.contains('active')
    ) {
        // is in mobile view
        // don't unwatch this field ..
        // the full screen div is being shown and the blur was forced as we need to hide the on-screen-keyboard
    } else if (isDropdownOpen()) {
        // field.onBlur is called before list.clicked so we need to defer hiding the list so that the list clicked event can be called.
        _timer = setTimeout(unwatchForReal, 500); // 10ms and 100ms was too quick for click handler :-(
    } else {
        unwatchForReal();
    }
}

function unwatchForReal() {
    if (!_input) return;

    const listItem = getFocusedListOption();
    const shadow = getShadowField(_input);
    const srcCode = _input.getAttribute('data-codefield');
    const srcDesc = _input.getAttribute('data-descfield');
    const format = _input.getAttribute('data-display-format');

    if (shadow && !_input.oldval) {
        console.warn(
            `Unable to "unwatch" ${_input.getAttribute('name')} which has not yet received focused`,
        );
        return;
    }

    let code;
    let desc;
    let parsedValue;
    let matches;

    if (listItem) {
        // the user has up/downed on a list option, and
        if (listItem.hasAttribute('data-ignore')) return;

        code = listItem.getAttribute(`data-${srcCode}`);
        desc = listItem.getAttribute(`data-${srcDesc}`);
        parsedValue = parseValue(code, desc, format);
        matches = _input.value === parsedValue; // the focused cell matches the text in the editable description field
    }

    if (!('oldval' in _input)) {
        console.warn(
            `Unable to cleanly unwatchForReal as field ${_input.getAttribute('id')} has not yet been focused.`,
        );
    } else if (matches) {
        const dirty = shadow
            ? _input.oldval.shadow !== code || _input.oldval.input !== parsedValue
            : _input.oldval !== parsedValue;

        if (dirty) {
            console.log('Applying previously focused option %s', _input.value);
            selectListOption(listItem, false);
        }
    } else if (shadow) {
        // When the user leaves the field ... they may have changed the value.
        // If the user has changed the value to something different we need to copy that raw value into the code
        // if the user has changed the value, and put it back, we need to restore the code value
        const dirty = _input.value !== _input.oldval.input;

        if (dirty) {
            // the visible field has changed (copy manually entered value into shadow field)
            shadow.value = _input.value;
            dispatch(_input, 'change');
            dispatch(shadow, 'change');
            dispatch(shadow, 'autocompleteshadowchange', {});
        } else {
            // we shouldn't need to cater for this situation, as it should only happen via the escape button which has the restore functionality
        }
    }

    delete _input.fixupPending; // let the page's _submit() method this field is finished

    // list.removeEventListener('click', handleListOptionClick);
    closeDropdown();
    // setFocused(false); document.body.classList[action]('autocomplete-visible');

    nullifyStateVariables();
}

function handleLoadMoreClicked(e) {
    e.preventDefault();
    e.stopPropagation();

    _input.page = _input.page || 1; // first page starts at "1", not "0".
    _input.page += 1;
    _options = null;
    syncSearchList(_input.page); // execute the search
}

// ---------------------------------------------------------------------------------------------
//
//   K E Y B O A R D   E V E N T   H A N D L E R S
//
// ---------------------------------------------------------------------------------------------

/**
 * Ignore Enter Key (form submit)
 */
function handleKeydownEvent(e) {
    const key = e.keyCode;
    if (_modifiers.indexOf(key) !== -1) return; // ignore initial modifier key presses

    if (key === KEYS.enter) {
        const pd =
            isBreakpointSmall() ||
            isDropdownOpen() ||
            e.target.getAttribute('data-submitOnEnter') === 'false';

        if (pd) {
            e.preventDefault(); // prevent the form from being submitted when the list is open
            return false;
        }
    }
}

/**
 * Get the results of the input[role=autocomplete] and fetch the results from the memory cache or xhr.response
 */
function handleKeyupEvent(e) {
    const input = e.target;
    _input = input;
    const key = e.keyCode;
    if (input.readOnly) return;
    if (_modifiers.indexOf(key) !== -1) return; // ignore initial modifier key presses

    const isUpDown = key === KEYS.up || key === KEYS.down;

    if (e.altKey && isUpDown) {
        // Toggle the listbox visibility if the user presses alt-up/down
        if (isDropdownOpen()) {
            closeDropdown();
        } else {
            openDropdown();
        }
    } else if (key === KEYS.escape) {
        restorePreviousValue();
        syncSearchButton(input);
        closeDropdown();
        input.select();
    } else if ((isUpDown || key === KEYS.enter) && isDropdownOpen()) {
        focusListOptionByKeyCode(key);
        if (key === KEYS.enter) closeDropdown();
    } else {
        input.page = 0;
        syncSearchButton(input);

        if (!isBreakpointSmall()) {
            // don't auto-search in mobile view ... the user has to press the search button (or press '<enter>')
            // setFocused();
            syncSearchList(); // auto-search on text input
        }
    }
}

// ---------------------------------------------------------------------------------------------
//
//   U t i l   F u n c t i o n s
//
// ---------------------------------------------------------------------------------------------

/**
 * Initialises data-params for given autocomplete field or updates with given params.
 * @param {HTMLInputElement} input - The autocomplete field.
 * @param {Object} [newParams] - The new params.
 */
function initParams(input, newParams) {
    const jsonParams = input.getAttribute('data-params-json');
    const srcParams = input.getAttribute('data-params-src');
    let params;

    if (newParams) {
        if (JSON.stringify(newParams) !== jsonParams) {
            params = jsonParams ? Object.assign(JSON.parse(jsonParams), newParams) : newParams;
            params = JSON.stringify(params);
            _cache[input.id] = {};
        }
    } else if (!srcParams) {
        params = input.getAttribute('data-params');
        if (params) input.setAttribute('data-params-src', params);
    } else if (srcParams !== jsonParams) {
        params = srcParams;
    }

    if (params) {
        input.setAttribute('data-params-json', params);

        try {
            params = JSON.parse(params);
            const arr = [];
            Object.keys(params).forEach((key) =>
                arr.push(`${key}=${encodeURIComponent(params[key])}`),
            );
            input.setAttribute('data-params', arr.join('&'));
        } catch (e) {
            console.warn(`Error converting JSON params to query params for field: ${input.id}`);
        }
    }
}

/**
 * Returns parsed value for given code and description.
 * @param {string} code - The code.
 * @param {string} desc - The description.
 * @param {string} format - The order in which the code and description should be displayed..
 * @returns {string} Parsed value for given code and description.
 */
function parseValue(code, desc, format) {
    if (desc && code) {
        if (format === 'code-descr') return `${code} (${desc})`;
        if (format === 'code') return `${code}`;
        if (format === 'descr') return `${desc}`;

        return `${desc} (${code})`; // descr-code
    }

    return desc || code;
}

/**
 * Convert the simple text field into a description field with a hidden field which gets sent to the server.
 *
 * @param  {[type]} node [description]
 * @return {[type]}      [description]
 */
function initShadowField(node) {
    // 1) place the description in the code field
    // 2) create a hidden field to hold the code value
    let shad = document.getElementById(node.getAttribute('data-shadow'));
    const filters = node.getAttribute('data-filters');

    if (shad) {
        // autocomplete filters in new survey table row modals may have a default value
        const format = node.getAttribute('data-display-format');

        if (filters) node.value = parseValue(node.value, node.getAttribute('data-display'), format);

        return shad;
    }

    shad = document.createElement('input');
    shad.type = 'hidden';
    shad.value = node.value;

    if (node.hasAttribute('data-display')) {
        const format = node.getAttribute('data-display-format');
        node.value = parseValue(node.value, node.getAttribute('data-display'), format);
    }

    shad.id = `${node.id}-shadow`;
    node.setAttribute('data-shadow', shad.id);

    shad.name = node.name;
    node.name = `${node.id}-description`; // required for data-propagate to work with shadowed fields

    if (filters) shad.setAttribute('data-filters', filters);
    node.parentNode.insertBefore(shad, node);
    return shad;
}

/**
 * Update the state of the dropdown based on the content of the input field.
 *
 * This method should be called when the content of the input field is changed.
 *
 * If the input field contains a valid search string (ie: the min chars are provided) a search will be performed.
 * On return of the request the results dropdown/<UL> will be shown.
 *
 * If the input field contains text, but the text is default/previously-valid text then a search for an empty
 * string (ie: the default list) will be searched (provided the field allows empty searching).
 *
 * @return {[type]} [description]
 */
function syncSearchList(page) {
    if (!page && _input.page) return;

    const input = _input;
    let text = _input.value;
    let url = computeUrl(page, text);

    let nodes = _cache[_input.id] && _cache[_input.id][url]; // results from previous search

    if (text === _input.lastSelectedDescription && (!nodes || nodes.length)) {
        // user hasn't entered any text ... and there are no results
        text = '';
        url = computeUrl(page, text);
        nodes = _cache[_input.id] && _cache[_input.id][url]; // display the results for the empty search
    }

    if (nodes) {
        _input.lastSearch = text;
        openDropdown(nodes, null, 1 < page); // show the dropdown list with these results
    } else if (getSearchMin(_input) <= text.length) {
        _input.lastSearch = text;
        const cachable = input.getAttribute('data-cachable') !== 'false'; // default to is cacheable, if "false" then it's not cacheable

        ajax({
            url,
            bypassProxy: !cachable,
            callback(response) {
                if (!response) return;

                _cache[input.id][url] = response.documentElement;

                if (!_input || text !== _input.lastSearch) {
                    console.log('Ignoring stale response (search="%s")', text);
                } else {
                    openDropdown(response.documentElement, input, 1 < page);
                }
            },
        });
    } else {
        return closeDropdown();
    }
}

function computeUrl(page, text) {
    const format = _input.getAttribute('data-display-format');
    let params = _input.getAttribute('data-params');
    params = params ? [params] : [];
    if (1 < page) params.push(`page=${page}`);
    if (format) params.push(`displayFormat=${format}`);
    params = params.length ? `?${params.join('&')}` : '';
    return `${_input.getAttribute('data-action')}/${encodeURIComponent(text)}${params}`;
}

/**
 * Update the state of the button (enabled/disabled) based on the content the input field and the min chars requirement.
 *
 * @param  {[type]} input  [description]
 * @return {[type]}        [description]
 */
function syncSearchButton(input) {
    const button = getButton(input, 'button.fa-magnifying-glass');
    if (!button) return;

    if (input.disabled) {
        button.setAttribute('disabled', 'disabled');
        return;
    }

    let { value } = input;
    const searchmin = getSearchMin(input);

    if (value === input.lastSelectedDescription) value = '';

    if (searchmin && value.length < searchmin) {
        button.setAttribute('disabled', 'disabled');
    } else {
        button.removeAttribute('disabled');
    }
}

function openDropdown(listItems, input, append = false) {
    input = input || _input; // when this method is called by ajax handler _input may not be correct for this handler, so use input from closure

    const list = getListElement(_input);
    const wrap = getListWrap(_input);

    if (listItems.tagName.toLowerCase() === 'html') {
        const uls = listItems.getElementsByTagName('ul');
        if (uls.length) listItems = uls[0];
    }

    if (listItems) {
        let node;
        let i = -1;

        if (!append) while ((node = list.childNodes[0])) list.removeChild(node);

        while ((node = listItems.childNodes[++i])) {
            if (node.nodeName.toLowerCase() === 'li') list.appendChild(node.cloneNode(true));
        }

        const hasRemainingRecords = $(list)
            .find('li[data-remaining]')
            .last()
            .attr('data-remaining'); // eg: "true" or "false"

        $(list.parentElement)
            .find('[data-ac-loadmore]')
            .attr('data-remaining', hasRemainingRecords);

        const hasResults =
            0 <
            $(list)
                .find('li')
                .filter((idx, el) => !el.hasAttribute('data-remaining')).length;

        if (!append && !hasResults) {
            list.innerHTML = '<li role="option" class="zero-records">No results found.</li>';
        }

        dispatch(input, 'listupdated', {
            list,
            search: input.lastSearch,
            page: input.page || 0,
        });
    }

    _input.setAttribute(ARIA_EXPANDED, true);

    list.setAttribute(ARIA_EXPANDED, true);
    util.showElement(list.parentElement);
    wrap.parentElement.classList.add('active');

    handleDropdownPosition(wrap);

    if (isBreakpointSmall() && !append) {
        disableBodyScroll();
        breakListOut(list);
    }

    if (isBreakpointSmall()) {
        _input.blur(); // remove focus from the input field to hide the on screen keyboard
        // ^^^ Note: unwatch() will be called but will result in a no-op as the list wrap is active and we're in mobile mode.
    }
    // @TODO: focus the first element in the list
}

/**
 * Handles the position of the dropdown if there is not enough space in the viewport below the input field
 */
function handleDropdownPosition(listContainer) {
    const viewPortHeight = window.innerHeight;
    const listBounds = listContainer.getBoundingClientRect();
    const spaceBelowField = viewPortHeight - listBounds.top;
    const isOutOfBounds = spaceBelowField < listBounds.height;

    if (isOutOfBounds) {
        listContainer.style.bottom = '100%';
        const extraSpace = 5;
        const maxHeight = 400;
        const menu = document.getElementById('menu');
        const menuHeight = menu ? menu.getBoundingClientRect().height : 0;
        const listContainerBottom = listContainer.getBoundingClientRect().bottom;
        const dropdownHeight = listContainerBottom - menuHeight - extraSpace;
        const dropdownMaxHeight = Math.min(dropdownHeight, maxHeight);
        listContainer.style.maxHeight = `${dropdownMaxHeight}px`;
    }
}

/**
 * Close the menu and clear the input text value
 *
 * @return {[type]} [description]
 */
function closeDropdown() {
    const list = getListElement(_input);
    const wrap = getListWrap(_input);

    _input.setAttribute(ARIA_EXPANDED, false);
    _input.page = 0;
    list.setAttribute(ARIA_EXPANDED, false);
    util.showElement(list.parentElement, false);
    wrap.parentElement.classList.remove('active');
    wrap.removeAttribute('style');
    restoreBodyScroll();
    breakListBackIn(list);

    if (isBreakpointSmall()) defocusListOptions();

    dispatch(_input, 'dropdownclosed');
}

function restorePreviousValue(input) {
    input = input || _input; // when called by restoreState() _input may be null, otherwise _input SHOULD be set.

    if (input.oldval.shadow != null) {
        input.value = input.oldval.input;
        getShadowField(input).value = input.oldval.shadow;
    } else {
        input.value = input.oldval;
    }
}

// ---------------------------------------------------------------------------------------------
//
//   L i s t   O p t i o n s
//
// ---------------------------------------------------------------------------------------------

function getFocusedListOption(returnIndex) {
    let index = -1;
    let node;
    const options = getOptions(_input);

    while ((node = options[++index])) {
        if (node.getAttribute(ARIA_SELECTED)) return returnIndex ? index : node;
    }

    return returnIndex ? -1 : null;
}

/**
 * Up/Down/Enter key press handler.
 *
 * Cycle through the options using the <up> and <down> keys, or return the selected element if <enter> pressed
 *
 * @param  {[type]} key [description]
 * @param  {[HTMLLIElement]} next [description]
 * @param  {[Array]} seen [description]
 * @return {[type]}     [description]
 */
function focusListOptionByKeyCode(key, next = null, seen = []) {
    const options = getOptions(_input);
    const len = options.length;

    let index = next || getFocusedListOption(true); // returns the index of the selected list item, or options.length if not found

    if (key === KEYS.up) {
        index = --index < 0 ? len - 1 : index;
    } else if (key === KEYS.down) {
        index = ++index < len ? index : 0;
    }

    let node = options[index];

    if (key === KEYS.enter) {
        selectListOption(node);
    } else if (node.hasAttribute('data-ignore')) {
        seen.push(node);

        node = !seen.includes(node) ? focusListOptionByKeyCode(key, node, seen) : null; // There must be only one or no list items.
    } else {
        focusListOption(node);
    }

    return node;
}

/**
 * Highlight the nominated list item
 *
 * @param  {[type]} node   [description]
 * @param  {[type]} select [description]
 * @return {[type]}        [description]
 */
function focusListOption(node, select) {
    select = 2 <= arguments.length ? !!select : true;

    defocusListOptions();

    if (!node) {
        // could happen when pressing up/down while the results list is empty
        console.log('Unable to focus list option null');
    } else {
        node.setAttribute(ARIA_SELECTED, true);

        const srcCode = _input.getAttribute('data-codefield');
        const srcDesc = _input.getAttribute('data-descfield');
        const format = _input.getAttribute('data-display-format');
        const code = node.getAttribute(`data-${srcCode}`);
        const desc = node.getAttribute(`data-${srcDesc}`);

        _input.value = node.hasAttribute('data-hide-code-field')
            ? parseValue('', desc, format)
            : parseValue(code, desc, format);
    }

    if (select) _input.select();
}

function defocusListOptions() {
    // Defocus previous list items / options
    const node = getFocusedListOption();
    if (node) node.removeAttribute(ARIA_SELECTED);
}

// selected option value in the autocomplete input
/**
 * [selectListOption description]
 * @param  {[type]} node   The list item that was selected
 * @param  {[type]} select set to false to prevent the input text field from being data-selected after value applied to field.
 * @return {[type]}        void
 */
function selectListOption(node, select) {
    focusListOption(node, select);

    const srcCode = _input.getAttribute('data-codefield');
    const srcDesc = _input.getAttribute('data-descfield');
    const format = _input.getAttribute('data-display-format');

    const code = node.getAttribute(`data-${srcCode}`); // eg: data-id
    const desc = node.getAttribute(`data-${srcDesc}`); // eg: data-display

    const shadow = getShadowField(_input);
    const value = node.hasAttribute('data-hide-code-field')
        ? parseValue('', desc, format)
        : parseValue(code, desc, format);

    if (shadow) {
        shadow.value = code;
        dispatch(shadow, 'change');

        const high = node.getAttribute('data-t106f025_organisation_unit_hi');
        const low = node.getAttribute('data-t106f015_organisation_unit_low');
        dispatch(shadow, 'autocompleteshadowchange', { high, low, value });
    }

    _input.value = value;
    dispatch(_input, 'autocompleteitemselected', node);
    dispatch(_input, 'change');

    Object.keys(_input['data-propagate'] || {}).forEach((src) => {
        const dest = _input['data-propagate'][src];
        const destField = document.getElementsByName(dest)[0];

        if (!destField) {
            console.warn(
                'Unable to propagate %s to %s, destination field does not exist',
                src,
                dest,
            );
        } else if (!node.hasAttribute(`data-${src}`)) {
            console.warn('Unable to propagate %s to %s, source value was not provided', src, dest);
        } else {
            destField.value = node.getAttribute(`data-${src}`);
            dispatch(destField, 'change');
        }
    });

    _input.lastSelectedDescription = _input.value;

    _input.oldval = shadow
        ? {
              shadow: shadow.value,
              input: _input.value,
          }
        : _input.value;

    syncSearchButton(_input);
}

function dispatch(field, eventname, detail) {
    field.dispatchEvent(new CustomEvent(eventname, { bubbles: true, detail: detail || field }));
}

let offset = null;
let scrollEl = null; // MSIE uses document.documentElement, chrome uses document.body

function disableBodyScroll() {
    if (document.documentElement && document.documentElement.scrollTop) {
        scrollEl = document.documentElement;
        offset = document.documentElement.scrollTop;
    } else {
        scrollEl = document.body;
        offset = document.body.scrollTop;
    }

    document.body.classList.add('autocomplete-visible');
}

function restoreBodyScroll() {
    document.body.classList.remove('autocomplete-visible');

    if (offset !== null) {
        scrollEl.scrollTop = offset;
        offset = null;
    }
}

function breakListOut(list) {
    const wrap = util.parent(list, '.search-list-wrap');
    const oldSibling = wrap.nextSibling;

    if (oldSibling) {
        wrap.restoreSibling = oldSibling;
    } else {
        wrap.restoreParent = wrap.parentNode;
    }

    wrap.classList.add('breakout');
    wrap.classList.add('form-selector');
    document.body.appendChild(wrap);
}

function breakListBackIn(list) {
    const wrap = util.parent(list, '.search-list-wrap');

    wrap.classList.remove('breakout');
    wrap.classList.remove('form-selector');

    if (wrap.restoreSibling) {
        wrap.restoreSibling.parentNode.insertBefore(wrap, wrap.restoreSibling);
    } else if (wrap.restoreParent) {
        wrap.restoreParent.appendChild(wrap);
    }

    delete wrap.restoreSibling;
    delete wrap.restoreParent;
}

// ---------------------------------------------------------------------------------------------
//
//   G e t t e r s   a n d   S e t t e r s
//
// ---------------------------------------------------------------------------------------------

function isBreakpointSmall() {
    return window.matchMedia
        ? !window.matchMedia(breakpoints.large).matches
        : window.innerWidth < 768;
}

function isDropdownOpen() {
    return _input && _input.getAttribute(ARIA_EXPANDED) === 'true';
}

function getSearchMin(input) {
    _searchmin = input.getAttribute('data-searchmin');
    _searchmin = _searchmin != null ? _searchmin * 1 : SEARCH_MIN_DEFAULT;
    return _searchmin;
}

function getButton(input, classQuery) {
    const baseEl = util.parent(input, '.form-selector, .mb-3');

    if (!baseEl) {
        console.warn('Could not find top level input field container');
        return;
    }

    _btn = baseEl.querySelector(classQuery);
    return _btn;
}

function getShadowField(input) {
    _shadow = document.getElementById(input.getAttribute('data-shadow')) || 0;
    return _shadow;
}

function getListElement(input) {
    if (!_list) {
        let listId;
        if (!(listId = input.getAttribute('aria-owns')))
            return console.error('field %s is missing attribute aria-owns', input.id);
        if (!(_list = document.getElementById(listId)))
            return console.error(
                'field %s references non-existent list container aria-owns=%s',
                input.id,
                listId,
            );
    }

    return _list;
}

function getListWrap(input) {
    const list = getListElement(input);
    if (list) return list.parentNode;
}

function getOptions(input) {
    if (!input) return [];

    if (!_options) {
        const list = getListElement(input);
        _options = list.querySelectorAll('[role=option]'); // the live list of search results as dom elements
    }

    return _options;
}

function getParams(input) {
    const params = input.getAttribute('data-params-json');

    try {
        return JSON.parse(params);
    } catch (e) {
        console.warn(`Unable to parse data-params-json [${params}]`);
    }

    return null;
}

function getValue(input, asObject = false) {
    const shadow = getShadowField(input);
    const value = (shadow && shadow.value) || input.value;

    if (asObject) {
        const description = input.lastSelectedDescription;
        return { description, value };
    }

    return value;
}

function setValue(input, value) {
    const obj = value instanceof Object ? value : { value };
    const shadow = getShadowField(input);

    if (shadow) {
        shadow.value = obj.value;
        input.value = obj.description;
    } else {
        input.value = obj.value;
    }
}

function getState(input) {
    const shadow = getShadowField(input);
    const oldVal = shadow
        ? {
              shadow: shadow.value, // the value
              input: input.value, // the description
          }
        : input.value;

    return {
        oldVal,
        dataParams: input.getAttribute('data-params'),
        dataParamsJson: input.getAttribute('data-params-json'),
        disabled: input.readOnly,
        lastSelectedDescription: input.lastSelectedDescription,
    };
}

function restoreState(input, state) {
    input.oldval = state.oldVal;
    input.lastSelectedDescription = state.lastSelectedDescription;
    input.setAttribute('data-params', state.dataParams);
    input.setAttribute('data-params-json', state.dataParamsJson);
    restorePreviousValue(input);
    setDisabled(input, state.disabled);
}

function setDisabled(input, disable = true) {
    const button = getButton(input, 'button.fa-magnifying-glass');

    if (disable) {
        input.setAttribute('readonly', 'true');
        if (button) button.setAttribute('disabled', '');
    } else {
        input.removeAttribute('readonly');
        if (button) button.removeAttribute('disabled');
    }
}

/**
 * Returns true if the input field is the visible input field (ie: not the shadow field).
 */
function isAutocompleteField(input) {
    return input.hasAttribute('autocomplete') && input.getAttribute('role') === 'combobox';
}

util.initialiseAddedNodes(
    (addedNodes) => {
        if (addedNodes.length === 1 && addedNodes[0] === document.body) {
            document.addEventListener('mousedown', (e) => handleMouseDown(e));
            document.addEventListener('wheel', (e) => handleMouseWheel(e));

            nullifyStateVariables();
        }

        init(addedNodes);
    },
    { observee: '#modal-content' },
);

const Autocomplete = {
    init,
    clear: clearAutocomplete,
    getParams,
    getValue,
    setValue,
    initParams,
    syncSearchButton,
    getState,
    restoreState,
    setDisabled,
    isAutocompleteField,
};

window.autocomplete = Autocomplete;

export default Autocomplete;

document.addEventListener('autocompleteshadowchange', updateFilters);

/**
 * Updates filtered autocomplete fields. Autocomplete fields with filters must have a params object
 * with the param to be updated as the first property. They must also have a placeholder in a
 * specific format, e.g. 'Search positions in and below Org Unit (1).' or 'Search all positions.'.
 * @param {Event} event - The event.
 */
function updateFilters(event) {
    const targetId = event.target.getAttribute && event.target.getAttribute('data-filters');

    if (targetId) {
        // event target is autocomplete filter so we need to update filters
        const target = document.getElementById(targetId);
        const matches = target.placeholder.match(/Search (.*) in|Search all (.*)[.]/);
        const type = matches[1] ? matches[1] : matches[2];
        let placeholder = `Search all ${type}.`;
        let value = '';

        if (event.detail.value) {
            // folder or org unit was selected
            if (event.detail.low && event.detail.high) {
                // org unit was selected
                placeholder = `Search positions in and below ${event.detail.value}.`;
                value = `\\>\\=${event.detail.low}\\&\\<\\=${event.detail.high}`;
            } else {
                // folder was selected
                placeholder = `Search forms in ${event.detail.value}...`;
                value = event.target.value;
            }
            util.showElement(document.getElementById(`${targetId}-filter-clear`), true);
        }

        target.placeholder = placeholder;
        const params = target.getAttribute('data-params-json');
        initParams(target, { [Object.keys(JSON.parse(params)).shift()]: value });
        clearAutocomplete(target);
    }
}

/**
 * Clears the given autocomplete field including its shadow field.
 * @param {Element} autocomplete - The autocomplete field.
 */
function clearAutocomplete(autocomplete) {
    autocomplete.lastSelectedDescription = '';
    autocomplete.oldval = { input: '', shadow: '' };
    autocomplete.value = '';

    const shadow = document.getElementById(autocomplete.getAttribute('data-shadow'));
    shadow.value = '';

    util.showElement(document.getElementById(`${autocomplete.id}-clear`), false);

    dispatch(autocomplete, 'change');
    dispatch(shadow, 'change');
    dispatch(shadow, 'autocompleteshadowchange', {});
}
